import moment from "moment-timezone";

// Pure helpers only (no setState / no notifications / no dispatch).

// Handles both timezone-aware (with Z, +, -) and timezone-naive (assumed UTC) strings
export const parseDateTimeInTimezone = (datetimeString, targetTimezone) => {
  if (!datetimeString) return null;
  try {
    if (
      datetimeString.includes("Z") ||
      datetimeString.includes("+") ||
      datetimeString.includes("-", 10)
    ) {
      return moment(datetimeString).tz(targetTimezone);
    }
    return moment.utc(datetimeString).tz(targetTimezone);
  } catch (_e) {
    return null;
  }
};

export const findObjectById = (id, officeShiftsList, jobShiftsList) => {
  if (!id) return null;
  if (Array.isArray(officeShiftsList)) {
    const officeShift = officeShiftsList.find(item => item.id === id);
    if (officeShift) return officeShift;
  }
  if (Array.isArray(jobShiftsList)) {
    const jobShift = jobShiftsList.find(item => item.id === id);
    if (jobShift) return jobShift;
  }
  return null;
};

export const findJobFromUnscheduledList = (id, unscheduledJobsList) => {
  if (!id || !Array.isArray(unscheduledJobsList)) return null;
  return unscheduledJobsList.find(job => job.id === id) || null;
};

export const determineType = object => {
  if (!object) return null;
  if (object.work_order?.id) return "job";
  if (object.office) return "office";
  return null;
};

export const validateFutureDate = dropDate => {
  if (!dropDate) return { isValid: false, error: "Drop date is required" };
  const dropMoment = moment(dropDate);
  const currentMoment = moment();
  if (dropMoment.format("YYYY-MM-DD") < currentMoment.format("YYYY-MM-DD")) {
    return {
      isValid: false,
      error: "Cannot drag to previous date. Only future dates are allowed.",
    };
  }
  return { isValid: true, error: null };
};

// Scenario 2: Calendar -> Unscheduled
export const createUnscheduledPayload = (object, selectedOrganization) => {
  if (!object || !selectedOrganization) return null;
  const type = determineType(object);
  if (type === "job") {
    return {
      organization_id: selectedOrganization.id,
      workOrder_id: object.work_order.id,
      job_id: object.id,
      status: "unscheduled",
    };
  }
  if (type === "office") {
    return {
      organization_id: selectedOrganization.id,
      shift_id: object.id,
      status: "unscheduled",
    };
  }
  return null;
};

// Scenario 3: Unscheduled -> Calendar (month cell drop)
export const createSchedulePayload = (
  object,
  dropDate,
  selectedOrganization,
) => {
  if (!object || !dropDate || !selectedOrganization) return null;
  const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
  const dropMoment = moment(dropDate);

  if (object.work_order?.id) {
    const dropDateInTz = moment.tz(dropMoment, orgTimezone);
    const requiredStartDate = dropDateInTz
      .clone()
      .startOf("day")
      .format("YYYY-MM-DDTHH:mm:ss");
    const requiredEndDate = dropDateInTz
      .clone()
      .endOf("day")
      .format("YYYY-MM-DDTHH:mm:ss");
    const startTime = moment
      .tz(
        dropDateInTz.format("YYYY-MM-DD") + " 00:00",
        "YYYY-MM-DD HH:mm",
        orgTimezone,
      )
      .format("YYYY-MM-DDTHH:mm:ss");
    const endTime = moment
      .tz(
        dropDateInTz.format("YYYY-MM-DD") + " 23:59",
        "YYYY-MM-DD HH:mm",
        orgTimezone,
      )
      .format("YYYY-MM-DDTHH:mm:ss");

    return {
      organization_id: selectedOrganization.id,
      workOrder_id: object.work_order.id,
      job_id: object.id,
      status: "scheduled",
      required_start_date: requiredStartDate,
      required_end_date: requiredEndDate,
      start_time: startTime,
      end_time: endTime,
      is_anytime: true,
    };
  }

  return null;
};

// Scenario 1: Calendar -> Calendar (reschedule)
export const createReschedulePayload = (
  object,
  dropDate,
  selectedOrganization,
) => {
  if (!object || !dropDate || !selectedOrganization) return null;

  const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
  const dropMoment = moment(dropDate);
  const type = determineType(object);

  if (type === "job") {
    const payload = {
      organization_id: selectedOrganization.id,
      workOrder_id: object.work_order.id,
      job_id: object.id,
    };

    if (object.is_anytime === false && object.start_time && object.end_time) {
      const existingStartTime = parseDateTimeInTimezone(
        object.start_time,
        orgTimezone,
      );
      const existingEndTime = parseDateTimeInTimezone(
        object.end_time,
        orgTimezone,
      );
      if (!existingStartTime || !existingEndTime) return null;

      const startHour = existingStartTime.hour();
      const startMinute = existingStartTime.minute();
      const startSecond = existingStartTime.second();
      const endHour = existingEndTime.hour();
      const endMinute = existingEndTime.minute();
      const endSecond = existingEndTime.second();

      const dropDateInTz = moment.tz(dropMoment, orgTimezone);
      const newStartTimeMoment = moment.tz(
        dropDateInTz.format("YYYY-MM-DD") +
          ` ${String(startHour).padStart(2, "0")}:${String(
            startMinute,
          ).padStart(2, "0")}:${String(startSecond).padStart(2, "0")}`,
        "YYYY-MM-DD HH:mm:ss",
        orgTimezone,
      );
      const newEndTimeMoment = moment.tz(
        dropDateInTz.format("YYYY-MM-DD") +
          ` ${String(endHour).padStart(2, "0")}:${String(endMinute).padStart(
            2,
            "0",
          )}:${String(endSecond).padStart(2, "0")}`,
        "YYYY-MM-DD HH:mm:ss",
        orgTimezone,
      );
      if (newEndTimeMoment.isSameOrBefore(newStartTimeMoment)) {
        newEndTimeMoment.add(1, "hour");
      }

      payload.start_time = newStartTimeMoment.format("YYYY-MM-DDTHH:mm:ss");
      payload.end_time = newEndTimeMoment.format("YYYY-MM-DDTHH:mm:ss");
    } else if (object.is_anytime === true) {
      const dropDateInTz = moment.tz(dropMoment, orgTimezone);
      payload.start_time = moment
        .tz(
          dropDateInTz.format("YYYY-MM-DD") + " 00:00",
          "YYYY-MM-DD HH:mm",
          orgTimezone,
        )
        .format("YYYY-MM-DDTHH:mm:ss");
      payload.end_time = moment
        .tz(
          dropDateInTz.format("YYYY-MM-DD") + " 23:59",
          "YYYY-MM-DD HH:mm",
          orgTimezone,
        )
        .format("YYYY-MM-DDTHH:mm:ss");
    }

    return payload;
  }

  if (type === "office") {
    const payload = {
      organization_id: selectedOrganization.id,
      shift_id: object.id,
      name: object.name || "",
      recurring: false,
      bulk: false,
      assignees: Array.isArray(object.assignees)
        ? object.assignees.map(a => a.id)
        : [],
    };

    if (object.description) payload.description = object.description;
    if (object.theme_color) payload.theme_color = object.theme_color;

    const dropDateInTz = moment.tz(dropMoment, orgTimezone);
    payload.required_start_date = dropDateInTz
      .clone()
      .startOf("day")
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss");
    payload.required_end_date = dropDateInTz
      .clone()
      .endOf("day")
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss");

    if (object.is_anytime === false && object.start_time && object.end_time) {
      const existingStartTime = parseDateTimeInTimezone(
        object.start_time,
        orgTimezone,
      );
      const existingEndTime = parseDateTimeInTimezone(
        object.end_time,
        orgTimezone,
      );
      if (!existingStartTime || !existingEndTime) return null;

      const startHour = existingStartTime.hour();
      const startMinute = existingStartTime.minute();
      const startSecond = existingStartTime.second();
      const endHour = existingEndTime.hour();
      const endMinute = existingEndTime.minute();
      const endSecond = existingEndTime.second();

      payload.start_time = moment
        .tz(
          dropDateInTz.format("YYYY-MM-DD") +
            ` ${String(startHour).padStart(2, "0")}:${String(
              startMinute,
            ).padStart(2, "0")}:${String(startSecond).padStart(2, "0")}`,
          "YYYY-MM-DD HH:mm:ss",
          orgTimezone,
        )
        .format("YYYY-MM-DDTHH:mm:ss");
      payload.end_time = moment
        .tz(
          dropDateInTz.format("YYYY-MM-DD") +
            ` ${String(endHour).padStart(2, "0")}:${String(endMinute).padStart(
              2,
              "0",
            )}:${String(endSecond).padStart(2, "0")}`,
          "YYYY-MM-DD HH:mm:ss",
          orgTimezone,
        )
        .format("YYYY-MM-DDTHH:mm:ss");
    } else if (object.is_anytime === true) {
      payload.start_time = moment
        .tz(
          dropDateInTz.format("YYYY-MM-DD") + " 00:00",
          "YYYY-MM-DD HH:mm",
          orgTimezone,
        )
        .format("YYYY-MM-DDTHH:mm:ss");
      payload.end_time = moment
        .tz(
          dropDateInTz.format("YYYY-MM-DD") + " 23:59",
          "YYYY-MM-DD HH:mm",
          orgTimezone,
        )
        .format("YYYY-MM-DDTHH:mm:ss");
    }

    return payload;
  }

  return null;
};

// Week/day time-slot drop payload
export const createTimeSlotPayload = (
  object,
  slotStart,
  slotEnd,
  selectedOrganization,
) => {
  if (!object || !slotStart || !slotEnd || !selectedOrganization) return null;
  const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
  const slotStartMoment = moment(slotStart);
  const slotEndMoment = moment(slotEnd);
  const type = determineType(object);

  if (type === "job") {
    const slotStartInTz = moment.tz(slotStartMoment, orgTimezone);
    const slotEndInTz = moment.tz(slotEndMoment, orgTimezone);
    return {
      organization_id: selectedOrganization.id,
      workOrder_id: object.work_order.id,
      job_id: object.id,
      status: "scheduled",
      required_start_date: slotStartInTz.format("YYYY-MM-DDTHH:mm:ss"),
      required_end_date: slotEndInTz.format("YYYY-MM-DDTHH:mm:ss"),
      start_time: slotStartInTz.format("YYYY-MM-DDTHH:mm:ss"),
      end_time: slotEndInTz.format("YYYY-MM-DDTHH:mm:ss"),
      is_anytime: false,
    };
  }

  if (type === "office") {
    const slotStartInTz = moment.tz(slotStartMoment, orgTimezone);
    const slotEndInTz = moment.tz(slotEndMoment, orgTimezone);

    const payload = {
      organization_id: selectedOrganization.id,
      shift_id: object.id,
      name: object.name || "",
      recurring: false,
      bulk: false,
      assignees: Array.isArray(object.assignees)
        ? object.assignees.map(a => a.id)
        : [],
    };

    if (object.description) payload.description = object.description;
    if (object.theme_color) payload.theme_color = object.theme_color;

    payload.required_start_date = slotStartInTz
      .clone()
      .startOf("day")
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss");
    payload.required_end_date = slotEndInTz
      .clone()
      .endOf("day")
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss");
    payload.start_time = slotStartInTz.format("YYYY-MM-DDTHH:mm:ss");
    payload.end_time = slotEndInTz.format("YYYY-MM-DDTHH:mm:ss");

    return payload;
  }

  return null;
};
